import { useMemo } from "react";
import { motion } from "motion/react";

// Rotating copy keeps loading screens feeling alive without being twee.
// Voice per PRD §11: warm, slightly literary, not therapy-voice, not metaphor-heavy.
const FULL_SCREEN_MESSAGES = [
  "Burrowing in...",
  "Down the rabbit hole...",
  "Pawing through the warren...",
  "Hopping along...",
  "Settling into the den...",
  "Sniffing around...",
  "Twitch of an ear...",
  "Combing the brush...",
  "Whiskers twitching...",
  "Nose to the ground...",
  "A patient little wait...",
  "Just a moment...",
  "Easing in...",
  "Picking up the scent...",
  "Rabbit's pace...",
];

const INLINE_MESSAGES = [
  "Tunneling...",
  "Hopping over...",
  "One ear up...",
  "Burrowing...",
  "Pawing through...",
  "Sniff...",
  "Nibble nibble...",
  "Nose wiggle...",
  "Twitch...",
];

// Themed sub-rotations for specific contexts. Pass `theme` to BurrowLoader to use.
const THEMED_MESSAGES: Record<string, string[]> = {
  edition: [
    "Opening the edition...",
    "Turning pages in the burrow...",
    "Smoothing the dust jacket...",
    "Reading along...",
  ],
  publish: [
    "Sending the edition out...",
    "Sealing the envelope...",
    "Slipping it under the door...",
    "Off it goes...",
  ],
  search: [
    "Searching the wider room...",
    "Sniffing through the warren...",
    "Wandering the warren...",
    "Peeking into the commons...",
  ],
};

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

interface BurrowLoaderProps {
  /** Optional explicit message; if omitted, one is picked randomly. */
  message?: string;
  /** Optional theme to pick a context-fitting rotation (e.g. "edition", "publish", "search"). */
  theme?: keyof typeof THEMED_MESSAGES;
}

/** Full-screen loader used by RootLayout and any pre-data view (profile, etc.). */
export function BurrowLoader({ message, theme }: BurrowLoaderProps = {}) {
  const msg = useMemo(() => {
    if (message) return message;
    if (theme && THEMED_MESSAGES[theme]) return pickRandom(THEMED_MESSAGES[theme]);
    // Reuse the line the static preloader already chose (see index.html), so the
    // text doesn't jump when React takes over — or between successive
    // full-screen loads in the same page session. Falls back to a fresh pick if
    // the stash is missing (e.g. loaded outside the app shell).
    const boot = typeof window !== "undefined"
      ? (window as unknown as { __burrowLoaderMsg?: string }).__burrowLoaderMsg
      : undefined;
    return boot || pickRandom(FULL_SCREEN_MESSAGES);
  }, [message, theme]);
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "var(--background, #F3EAE0)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 24,
      }}
    >
      <motion.div
        animate={{ y: [0, -18, 0, -10, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src="/favicon.png" alt="" style={{ width: 48, height: 48, opacity: 0.6 }} />
      </motion.div>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        style={{
          fontFamily: "'Fraunces', Georgia, serif",
          fontSize: "0.88rem",
          fontStyle: "italic",
          color: "var(--muted, #8A7668)",
          // Legible enough to catch the rotating copy, still soft enough to
          // feel like an easter egg rather than a headline.
          opacity: 0.75,
        }}
      >
        {msg}
      </motion.p>
    </div>
  );
}

/** Inline loader for panes/sheets — sized to fit a content area, not the page. */
export function InlineBurrowLoader({ message, theme }: BurrowLoaderProps = {}) {
  const msg = useMemo(() => {
    if (message) return message;
    if (theme && THEMED_MESSAGES[theme]) return pickRandom(THEMED_MESSAGES[theme]);
    return pickRandom(INLINE_MESSAGES);
  }, [message, theme]);
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-3">
      <motion.div
        animate={{ y: [0, -10, 0, -5, 0] }}
        transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <img src="/favicon.png" alt="" style={{ width: 28, height: 28, opacity: 0.55 }} />
      </motion.div>
      <span
        className="text-muted-foreground/75 italic"
        style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "0.82rem" }}
      >
        {msg}
      </span>
    </div>
  );
}
